import { Environment, Environments } from 'Environment';
import { merge } from 'lodash';
import { errorMessages } from 'utils';
import { toastMessageServiceSingleton } from './toast-message.service';

export async function request(
  url = '',
  options = {
    fetchOptions: {},
    tealiumErrorLogger: null,
    showToastOnError: true,
    suppressTealiumEventOnError: false,
    ignoreErrorCodes: [],
  },
) {
  const ALLOWED_HOSTS = Object.values(Environments)
    .map((env) => {
      try {
        return new URL(env.apiHost).hostname;
      } catch {
        return null;
      }
    })
    .filter(Boolean);

  const buildBaseUrl = () => {
    const apiHost = Environment.current?.apiHost || '';
    const apiBase = Environment.current?.apiBase || '';
    const combined = apiHost.endsWith('/') ? apiHost.slice(0, -1) : apiHost;
    const base = apiBase.startsWith('/') ? apiBase.slice(1) : apiBase;
    return `${combined}/${base}/`;
  };

  const requestBaseUrl = buildBaseUrl();

  const { tealiumErrorLogger, showToastOnError, suppressTealiumEventOnError, ignoreErrorCodes, baseURLOverwrite } =
    merge(
      {
        tealiumErrorLogger: null,
        showToastOnError: true,
        suppressTealiumEventOnError: false,
        ignoreErrorCodes: [],
        baseURLOverwrite: null,
      },
      options,
    );

  const validatedUrl = validateAndSanitizeUrl(url, baseURLOverwrite || requestBaseUrl, ALLOWED_HOSTS);

  const controller = new AbortController();
  options.fetchOptions = options.fetchOptions || {};
  let { fetchOptions } = options;
  fetchOptions.method = fetchOptions.method || 'GET';
  fetchOptions.headers = fetchOptions.headers || {};
  fetchOptions.headers.Accept = 'application/json';
  fetchOptions.signal = controller.signal;
  //  fetchOptions.headers['Content-Type'] = fetchOptions.headers['Content-Type'] || 'application/json';

  if (fetchOptions.headers['Content-Type'] === 'multipart/form-data') {
    // server doesn't like these set for form-data
    delete fetchOptions.headers['Content-Type'];
    delete fetchOptions.headers['Accept'];
  }
  // Timeout after 30s
  setTimeout(() => controller.abort(), 30000);

  try {
    const res = await fetch(validatedUrl, fetchOptions);
    const body = res.status === 204 ? {} : await res.json();
    const result = body?.result || body; /* SF */

    if (result && !ignoreErrorCodes.some((ec) => res.status === ec) && res.status >= 400) {
      let message = errorMessages.technical.general;

      // LLL
      if (result.Message) {
        message = result.Message;
      }
      if (showToastOnError && (!fetchOptions.signal || !fetchOptions.signal.aborted)) {
        toastMessageServiceSingleton.addError(
          message,
          null,
          `${res.status}`,
          false,
          null,
          suppressTealiumEventOnError,
        );
      }
      if (tealiumErrorLogger) {
        tealiumErrorLogger(message);
      }
    }

    return { status: res.status, result: result };
  } catch (e) {
    console.warn(validatedUrl, e);
    return {
      status: 500,
      errorMsg: errorMessages.technical.general,
    };
  }
}

// Extract validation logic into separate function
function validateAndSanitizeUrl(url, baseUrl, allowedHosts) {
  // Reject full URLs from user input
  if (url.startsWith('http://') || url.startsWith('https://')) {
    throw new Error('Full URLs are not allowed in request path');
  }

  // Safely construct full URL
  try {
    const parsedUrl = new URL(url, baseUrl);

    // Enforce HTTPS only
    if (parsedUrl.protocol !== 'https:') {
      throw new Error('Only HTTPS is allowed');
    }

    // Enforce host allow-list
    if (!allowedHosts.includes(parsedUrl.hostname)) {
      throw new Error(`Blocked unsafe host: ${parsedUrl.hostname}`);
    }

    return parsedUrl.toString();
  } catch (error) {
    throw new Error(`Invalid URL path: ${error.message}`);
  }
}
